import { dateFormatFrenchPretty, convertDateToElasticTimeStamp, dateFormatFrenchPrettyWithMinutes } from "@/utils/dates";

// Delta is evaluated once for all selected counters. Only period totals leave
// Elasticsearch; the chronological working data remains inside the cluster.
const DELTA_AGGREGATION_NAME = "__delta_cycles";

const DELTA_MAP_SCRIPT = `
    if (!doc.containsKey(params.date_field) || doc[params.date_field].size() == 0) return;
    long ts = doc[params.date_field].value.toInstant().toEpochMilli();
    String series = '';
    for (def field : params.series_fields) {
        if (!doc.containsKey(field) || doc[field].size() != 1) {
            throw new IllegalArgumentException('Delta requires a single series identifier: ' + field);
        }
        String part = doc[field].value.toString();
        series += part.length() + ':' + part;
    }
    def values = new ArrayList();
    boolean present = false;
    for (def field : params.fields) {
        if (!doc.containsKey(field) || doc[field].size() == 0) {
            values.add(null);
            continue;
        }
        if (doc[field].size() != 1) {
            throw new IllegalArgumentException('Delta requires a scalar counter: ' + field);
        }
        double value = (double) doc[field].value;
        if (Double.isNaN(value) || Double.isInfinite(value) || value < 0.0) {
            throw new IllegalArgumentException('Delta requires a finite non-negative counter: ' + field);
        }
        values.add(value);
        present = true;
    }
    if (!present) return;
    long bucket;
    if (params.bucket_type == 'fixed') {
        long width = (long) params.bucket_millis;
        long remainder = ts % width;
        bucket = ts - remainder;
        if (remainder < 0) bucket -= width;
    } else {
        def dt = ZonedDateTime.ofInstant(Instant.ofEpochMilli(ts), ZoneId.of('UTC'));
        dt = dt.withNano(0).withSecond(0);
        if (params.bucket_unit != 'minute') dt = dt.withMinute(0);
        if (params.bucket_unit != 'minute' && params.bucket_unit != 'hour') dt = dt.withHour(0);
        if (params.bucket_unit == 'week') dt = dt.minusDays(dt.getDayOfWeek().getValue() - 1);
        if (params.bucket_unit == 'month') dt = dt.withDayOfMonth(1);
        if (params.bucket_unit == 'quarter') dt = dt.withDayOfMonth(1).withMonth(((dt.getMonthValue() - 1) / 3) * 3 + 1);
        if (params.bucket_unit == 'year') dt = dt.withDayOfMonth(1).withMonth(1);
        if (params.bucket_unit == 'second') {
            bucket = ts - (ts % 1000);
            if (ts % 1000 < 0) bucket -= 1000;
        } else {
            bucket = dt.toInstant().toEpochMilli();
        }
    }
    state.points.add([series, ts, bucket, values]);
`;

const DELTA_COMBINE_SCRIPT = `
    state.points.sort((a, b) -> {
        int group = a[0].compareTo(b[0]);
        if (group != 0) return group;
        return a[1] < b[1] ? -1 : (a[1] > b[1] ? 1 : 0);
    });
    return state.points;
`;

const DELTA_REDUCE_SCRIPT = `
    boolean cursorBefore(def a, def b) {
        def left = a[0].get(a[1]);
        def right = b[0].get(b[1]);
        int group = left[0].compareTo(right[0]);
        return group < 0 || (group == 0 && left[1] < right[1]);
    }
    void credit(def totals, long bucket, long ts, double peak, int field, def config) {
        if (config.from != null && ts < (long) config.from) return;
        if (config.to != null && ts > (long) config.to) return;
        String key = Long.toString(bucket);
        def values = totals.get(key);
        if (values == null) {
            values = new ArrayList();
            for (int i = 0; i < config.fields.size(); i++) values.add(0.0);
            totals.put(key, values);
        }
        values.set(field, (double) values.get(field) + peak);
    }
    void finishOpen(def stats, def totals, def config) {
        if (!config.include_open_cycle) return;
        for (int f = 0; f < config.fields.size(); f++) {
            if (stats.peaks[f] > 0.0) {
                credit(totals, stats.peak_buckets[f], stats.peak_times[f], stats.peaks[f], f, config);
            }
        }
    }
    void consume(def point, def stats, def totals, def config) {
        if (stats.series == null || stats.series != point[0]) {
            if (stats.series != null) finishOpen(stats, totals, config);
            stats.series = point[0];
            int length = config.fields.size();
            stats.previous = new double[length];
            stats.peaks = new double[length];
            stats.peak_times = new long[length];
            stats.peak_buckets = new long[length];
        }
        for (int f = 0; f < config.fields.size(); f++) {
            if (point[3][f] == null) continue;
            double current = (double) point[3][f];
            double previous = stats.previous[f];
            double peak = stats.peaks[f];
            // Every strict decrease closes the previous monotonic run.
            // A plateau stays within the same cycle; zero closes only once.
            boolean reset = previous > 0.0 && current < previous;
            if (reset && peak > 0.0) {
                long at = config.attribution == 'reset' ? (long) point[1] : stats.peak_times[f];
                long bucket = config.attribution == 'reset' ? (long) point[2] : stats.peak_buckets[f];
                credit(totals, bucket, at, peak, f, config);
                stats.peaks[f] = 0.0;
            }
            if (current > 0.0 && current >= stats.peaks[f]) {
                stats.peaks[f] = current;
                stats.peak_times[f] = (long) point[1];
                stats.peak_buckets[f] = (long) point[2];
            }
            stats.previous[f] = current;
        }
    }

    // Merge already sorted shard streams using a min-heap. No second complete
    // list and no global re-sort on the coordinating node.
    def heap = new ArrayList();
    for (def shard : states) {
        if (shard == null || shard.isEmpty()) continue;
        heap.add([shard, 0]);
        int at = heap.size() - 1;
        while (at > 0) {
            int parent = (at - 1) / 2;
            if (!cursorBefore(heap.get(at), heap.get(parent))) break;
            def swap = heap.get(parent);
            heap.set(parent, heap.get(at));
            heap.set(at, swap);
            at = parent;
        }
    }
    def totals = new HashMap();
    def stats = ['series': null];
    def pending = null;
    while (!heap.isEmpty()) {
        def cursor = heap.get(0);
        def point = cursor[0].get(cursor[1]);
        cursor[1] = (int) cursor[1] + 1;
        if (cursor[1] >= cursor[0].size()) {
            def last = heap.remove(heap.size() - 1);
            if (!heap.isEmpty()) heap.set(0, last);
        }
        int at = 0;
        while (at * 2 + 1 < heap.size()) {
            int child = at * 2 + 1;
            if (child + 1 < heap.size() && cursorBefore(heap.get(child + 1), heap.get(child))) child++;
            if (!cursorBefore(heap.get(child), heap.get(at))) break;
            def swap = heap.get(at);
            heap.set(at, heap.get(child));
            heap.set(child, swap);
            at = child;
        }
        if (pending != null && pending[0] == point[0] && pending[1] == point[1]) {
            for (int f = 0; f < params.fields.size(); f++) {
                if (point[3][f] == null) continue;
                if (pending[3][f] == null) pending[3][f] = point[3][f];
                else if ((double) pending[3][f] != (double) point[3][f]) {
                    throw new IllegalArgumentException('Conflicting Delta values at the same timestamp; provide distinct series_fields or fix duplicate documents');
                }
            }
        } else {
            if (pending != null) consume(pending, stats, totals, params);
            pending = [point[0], point[1], point[2], new ArrayList(point[3])];
        }
    }
    if (pending != null) {
        consume(pending, stats, totals, params);
        finishOpen(stats, totals, params);
    }
    def result = new HashMap();
    for (def entry : totals.entrySet()) {
        def columns = new HashMap();
        for (def output : params.outputs) {
            double sum = 0.0;
            for (def field : output.indices) sum += (double) entry.getValue().get((int) field);
            columns.put(output.name, sum);
        }
        result.put(entry.getKey(), columns);
    }
    return ['columns': params.output_names, 'buckets': result];
`;

const buildDeltaBucketParams = (esInterval) => {
    if (esInterval.type === "fixed") {
        const match = /^(\d+)(ms|s|m|h|d)$/.exec(esInterval.value);
        if (!match || Number(match[1]) <= 0) throw new Error("Unsupported Delta fixed interval");
        const units = { ms: 1, s: 1000, m: 60000, h: 3600000, d: 86400000 };
        return { bucket_type: "fixed", bucket_millis: Number(match[1]) * units[match[2]] };
    }
    const units = {
        second: "second", "1s": "second", minute: "minute", "1m": "minute",
        hour: "hour", "1h": "hour", day: "day", "1d": "day",
        week: "week", "1w": "week", month: "month", "1M": "month",
        quarter: "quarter", "1q": "quarter", year: "year", "1y": "year"
    };
    if (!units[esInterval.value]) throw new Error("Unsupported Delta calendar interval: " + esInterval.value);
    return { bucket_type: "calendar", bucket_unit: units[esInterval.value] };
};

const buildDeltaAggregation = (requests, esInterval, options, from, to) => {
    const fields = [...new Set(requests.reduce((all, request) => all.concat(request.fields), []))];
    const seriesFields = options.seriesFields === undefined ? ["Site"] : options.seriesFields;
    if (!Array.isArray(seriesFields) || seriesFields.some(field => typeof field !== "string" || !field.trim())) {
        throw new Error("Delta seriesFields must be an array of doc-value field names");
    }
    const supportedOptions = ["seriesFields", "attribution", "includeOpenCycle", "contextStartDate"];
    for (const key of Object.keys(options)) {
        if (!supportedOptions.includes(key)) throw new Error("Unsupported Delta option: " + key);
    }
    if (requests.some(request => request.fields.length === 0)) throw new Error("Delta requires at least one counter per output");
    const attribution = options.attribution === undefined ? "peak" : options.attribution;
    if (attribution !== "peak" && attribution !== "reset") throw new Error("Delta attribution must be peak or reset");
    if (options.includeOpenCycle !== undefined && typeof options.includeOpenCycle !== "boolean") throw new Error("Delta includeOpenCycle must be boolean");
    if (fields.some(field => typeof field !== "string" || !field.trim())) throw new Error("Delta requires non-empty field names");
    return {
        scripted_metric: {
            params: {
                date_field: "@timestamp", fields, series_fields: [...new Set(seriesFields)],
                outputs: requests.map(request => ({ name: request.name, indices: request.fields.map(field => fields.indexOf(field)) })),
                output_names: requests.map(request => request.name),
                attribution, include_open_cycle: options.includeOpenCycle === true, from, to,
                ...buildDeltaBucketParams(esInterval)
            },
            init_script: "state.points = new ArrayList();",
            map_script: DELTA_MAP_SCRIPT,
            combine_script: DELTA_COMBINE_SCRIPT,
            reduce_script: DELTA_REDUCE_SCRIPT
        }
    };
};

/**
 * Apply immediately after a Delta search. Only copies server-computed period
 * totals into the existing time_buckets[].<csv_column>.value format.
 * Supports a raw response, the Elasticsearch client {body}, or Axios {data}.
 * Non-Delta responses are returned unchanged. No raw-point calculation here.
 */
export const applyDeltaAggregations = (response) => {
    if (response && response.body && response.body.aggregations) {
        const body = applyDeltaAggregations(response.body);
        return body === response.body ? response : { ...response, body };
    }
    if (response && response.data && response.data.aggregations) {
        const data = applyDeltaAggregations(response.data);
        return data === response.data ? response : { ...response, data };
    }
    const aggregations = response && response.aggregations;
    if (!aggregations || !Object.prototype.hasOwnProperty.call(aggregations, DELTA_AGGREGATION_NAME)) return response;
    if (response.timed_out || (response._shards && response._shards.failed > 0)) {
        throw new Error("Incomplete Elasticsearch Delta search; do not display partial cycle totals");
    }
    const container = aggregations[DELTA_AGGREGATION_NAME];
    const delta = container && (container.value || (container.scope && container.scope.totals && container.scope.totals.value));
    if (!delta || !Array.isArray(delta.columns) || !delta.buckets || !aggregations.time_buckets || !Array.isArray(aggregations.time_buckets.buckets)) {
        throw new Error("Unexpected Elasticsearch Delta aggregation response");
    }
    const buckets = aggregations.time_buckets.buckets.map(bucket => {
        const values = delta.buckets[String(bucket.key)] || {};
        const result = { ...bucket };
        for (const name of delta.columns) {
            const value = Object.prototype.hasOwnProperty.call(values, name) ? values[name] : 0;
            if (typeof value !== "number" || !Number.isFinite(value)) throw new Error("Invalid Elasticsearch Delta total");
            Object.defineProperty(result, name, { value: { value }, enumerable: true, writable: true, configurable: true });
        }
        return result;
    });
    const resultAggs = { ...aggregations, time_buckets: { ...aggregations.time_buckets, buckets } };
    delete resultAggs[DELTA_AGGREGATION_NAME];
    return { ...response, aggregations: resultAggs };
};
// Plages réellement affichées, partagées avec le bouton d'export existant.
// Une instance de graphique possède sa plage ; elle la retire à sa destruction.
const visualizationDownloadRanges = new Map();

export const beginVisualizationDownloadRanges = (siteId, owner, generation) => {
    const key = String(siteId);
    if (!visualizationDownloadRanges.has(key)) visualizationDownloadRanges.set(key, new Map());
    visualizationDownloadRanges.get(key).set(owner, { generation, ranges: null });
};

export const setVisualizationDownloadRanges = (siteId, owner, generation, ranges) => {
    const entry = visualizationDownloadRanges.get(String(siteId))?.get(owner);
    // Une ancienne réponse ou un graphique démonté ne remplace pas la sélection.
    if (!entry || entry.generation !== generation) return;
    entry.ranges = ranges.map(range => ({ ...range }));
};

export const clearVisualizationDownloadRanges = (siteId, owner) => {
    const key = String(siteId);
    const owners = visualizationDownloadRanges.get(key);
    if (!owners) return;
    owners.delete(owner);
    if (owners.size === 0) visualizationDownloadRanges.delete(key);
};

export const buildVisualizationDownloadRange = (startDate, endDate, dataTimeOffset, endPrecision = date => date) => {
    const start = new Date(startDate);
    let end = new Date(endDate);
    const offset = Number(dataTimeOffset || 0);
    // Même décalage que les mesures du graphique, sans son intervalle antérieur.
    start.setHours(start.getHours() - offset);
    end.setHours(end.getHours() - offset);
    end = endPrecision(end);
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || start > end) {
        throw new Error("La période du graphique est invalide.");
    }
    return {
        gte: getElasticTimestamp(start, "@timestamp"),
        lte: getElasticTimestamp(end, "@timestamp"),
    };
};

export const buildVisualizationDownloadQuery = (siteId, childrenSiteIds, timesort) => {
    const owners = visualizationDownloadRanges.get(String(siteId));
    if (!owners || owners.size === 0) {
        throw new Error("Affichez un graphique avant de télécharger ses mesures.");
    }
    const ranges = new Map();
    for (const entry of owners.values()) {
        if (entry.ranges === null) {
            throw new Error("Le graphique se met à jour. Réessayez après son chargement.");
        }
        for (const range of entry.ranges) ranges.set(JSON.stringify(range), range);
    }
    if (ranges.size === 0) {
        throw new Error("Aucune période affichée n'est disponible pour l'export.");
    }
    const clauses = [...ranges.values()].map(range => ({ range: { "@timestamp": { ...range } } }));
    const elasticQuery = buildQuery(siteId, childrenSiteIds, timesort);
    elasticQuery.query.bool.filter = clauses.length === 1 ? clauses : [{
        bool: { should: clauses, minimum_should_match: 1 },
    }];
    return elasticQuery;
};

export const buildQuery = (site_id, children_site_ids, timesort) => {

    let elasticQueryMust = buildQueryMust(site_id, children_site_ids);

    let elasticQuery = { query: { bool: { must: elasticQueryMust } } };

    if (timesort) {
        let elasticQuerySort = {
            sort: [
                {
                    "@timestamp": {
                        order: timesort
                    }
                }
            ]
        }
        elasticQuery = { ...elasticQuery, ...elasticQuerySort };
    }

    return elasticQuery;
}

export const buildSortSizeMoreQuery = (site_id, children_site_ids, timesort, mesure, maxValue) => {

    let elasticQueryMust = buildQueryMust(site_id, children_site_ids);
    let elasticQuery = { query: { bool: { must: elasticQueryMust } } };
    let elasticDateFormat = dateFormatFrenchPretty;

    if (timesort) {
        let elasticQuerySort = {
            sort: [
                {
                    "@timestamp": {
                        order: timesort
                    }
                }
            ],
            _source: mesure
        }

        elasticQuery.query.bool.filter = [{
            range: {}
        }];

        elasticQuery.query.bool.filter[0].range[mesure] = {
            gt: maxValue
        };

        elasticQuery = { ...elasticQuery, ...elasticQuerySort };
    }

    return elasticQuery;
}

export const buildSortSizeQuery = (site_id, children_site_ids, timesort, mesure) => {

    let elasticQueryMust = buildQueryMust(site_id, children_site_ids);
    let elasticQuery = { query: { bool: { must: elasticQueryMust } } };
    let elasticDateFormat = dateFormatFrenchPretty;

    if (timesort) {
        let elasticQuerySort = {
            sort: [
                {
                    "@timestamp": {
                        order: timesort
                    }
                }
            ],
            _source: mesure
        }

        elasticQuery = { ...elasticQuery, ...elasticQuerySort };
    }

    return elasticQuery;
}

export const buildQueryMust = (site_id, children_site_ids, filters) => {
    let elasticQueryMust = [];

    if (children_site_ids && children_site_ids.length > 0) {
        let site_ids = [...children_site_ids, site_id];
        elasticQueryMust.push({ terms: { Site: site_ids } });
    } else {
        elasticQueryMust = [{ term: { Site: site_id } }]
    }

    if (filters && filters.parcelles && filters.parcelles.length > 0) {
        elasticQueryMust.push(
            {
                bool: {should: [
                    {
                        "terms": {
                            "Parcelle_name.keyword": filters.parcelles
                        }
                    },
                    {
                        "terms": {
                            "Parcelle.keyword": filters.parcelles
                        }
                    }
                ]}
            }
        );
    }

    if (filters && filters.cepages && filters.cepages.length > 0) {
        elasticQueryMust.push({
            "terms": {
                "Cepage.keyword": filters.cepages
            }
        });
    }

    if (filters && filters.traitements && filters.traitements.length > 0) {
        elasticQueryMust.push({
            "terms": {
                "TypeMaladie.keyword": filters.traitements
            }
        });
    }

    return elasticQueryMust;
}

export const buildTimeRangeQuery = (
    startDate,
    endDate,
    interval,
    site_id,
    columns,
    filters,
    children_site_ids,
    eventsOnly = false,
    deltaOptions = {}
) => {
    let elasticQuery = {};
    let elasticQueryMust = buildQueryMust(site_id, children_site_ids, filters);

    let elasticQueryFilter = [];
    if (eventsOnly) {
        elasticQueryFilter.push({ exists: { field: "events" } });
    }

    const dateField = "@timestamp";

    if (startDate || endDate) {
        if (startDate) {
            const start = Object.assign(startDate);
            const gte = getElasticTimestamp(start, dateField);
            elasticQueryFilter.push({ range: { [dateField]: { gte } } });
        }

        if (endDate) {
            const end = Object.assign(endDate);
            const lte = getElasticTimestamp(end, dateField);
            elasticQueryFilter.push({ range: { [dateField]: { lte } } });
        } else {
            endDate = new Date();
        }
    }

    const mapIntervalToES = (i) => {
        if (!i) return null;
        if (i === "minute") return { type: "fixed", value: "1m" };
        if (i === "hour") return { type: "fixed", value: "1h" };
        if (i === "day") return { type: "calendar", value: "1d" };
        if (i === "week") return { type: "calendar", value: "1w" };
        if (i === "month") return { type: "calendar", value: "1M" };
        if (i === "year") return { type: "calendar", value: "1y" };
        return { type: "calendar", value: i };
    };

    const mapDateFormat = (i) => {
        if (i === "hour" || i === "minute") return dateFormatFrenchPrettyWithMinutes;
        return dateFormatFrenchPretty;
    };

    const buildStandardAggType = (aggregationsType) => {
        if (aggregationsType === "Moyenne") return "avg";
        if (aggregationsType === "Cumul") return "sum";
        if (aggregationsType === "Maximum") return "max";
        if (aggregationsType === "Min/Max") return "avg";
        if (aggregationsType === "Delta") return "delta";
        return "avg";
    };

    // Share one chronological Delta calculation across the selected columns.
    const deltaRequests = [];

    let elasticQueryAggs;

    if (interval && interval !== "data") {
        const esInterval = mapIntervalToES(interval);
        const elasticDateFormat = mapDateFormat(interval);

        const timeHistogram = {
            field: dateField,
            format: elasticDateFormat,
        };

        if (esInterval.type === "fixed") {
            timeHistogram.fixed_interval = esInterval.value;
        } else {
            timeHistogram.calendar_interval = esInterval.value;
        }

        elasticQueryAggs = {
            time_buckets: {
                date_histogram: timeHistogram,
                aggs: {},
            },
        };

        for (let k in columns) {
            const displayedSensor = columns[k];

            // -------------------------
            // COMBINED SENSOR
            // -------------------------
            if (displayedSensor.isCombined && displayedSensor.combinedColumns && displayedSensor.combinedColumns.length > 0) {
                const aggType = buildStandardAggType(displayedSensor.aggregationsType);
                const combinedColumnsArray = displayedSensor.combinedColumns.split(",").map(col => col.trim());

                if (aggType === "delta") {
                    deltaRequests.push({
                        name: displayedSensor.csv_column,
                        fields: [...new Set(combinedColumnsArray.filter(Boolean))]
                    });
                    continue;
                }

                // NON-DELTA combined
                let esMetric = aggType === "avg" || aggType === "sum" || aggType === "max" ? aggType : "avg";

                let buckets_path = {};
                for (let col of combinedColumnsArray) {
                    elasticQueryAggs.time_buckets.aggs[col + "_source"] = {
                        [esMetric]: { field: col }
                    };
                    buckets_path[col] = col + "_source";
                }

                const scriptParts = combinedColumnsArray.map(col => `(params.${col} != null ? params.${col} : 0)`);
                elasticQueryAggs.time_buckets.aggs[displayedSensor.csv_column] = {
                    bucket_script: {
                        buckets_path,
                        script: scriptParts.join(" + ")
                    }
                };

                continue;
            }

            // -------------------------
            // STANDARD SENSOR
            // -------------------------
            let aggType = buildStandardAggType(displayedSensor.aggregationsType);
            let includeMinMax = (displayedSensor.aggregationsType === "Min/Max") || (displayedSensor.includeMinMax === true);

            if (aggType === "delta") {
                deltaRequests.push({ name: displayedSensor.csv_column, fields: [displayedSensor.csv_column] });
                continue;
            }

            // NON-DELTA
            let esMetric = aggType === "avg" || aggType === "sum" || aggType === "max" ? aggType : "avg";

            elasticQueryAggs.time_buckets.aggs[displayedSensor.csv_column] = {
                [esMetric]: { field: displayedSensor.csv_column }
            };

            if (includeMinMax) {
                elasticQueryAggs.time_buckets.aggs[displayedSensor.csv_column + "_min"] = {
                    min: { field: displayedSensor.csv_column }
                };
                elasticQueryAggs.time_buckets.aggs[displayedSensor.csv_column + "_max"] = {
                    max: { field: displayedSensor.csv_column }
                };
            }
        }
    }

    elasticQuery = {
        ...elasticQuery,
        query: { bool: { must: elasticQueryMust, filter: elasticQueryFilter } },
    };

    if (elasticQueryAggs) {
        elasticQuery = { ...elasticQuery, aggs: elasticQueryAggs };
    }

    if (deltaRequests.length > 0) {
        if (!deltaOptions || typeof deltaOptions !== "object" || Array.isArray(deltaOptions)) throw new Error("Invalid Delta options");
        const toMilliseconds = value => {
            if (value === undefined || value === null) return null;
            const ms = new Date(value).getTime();
            if (!Number.isFinite(ms)) throw new Error("Invalid Delta date boundary");
            return ms;
        };
        const from = toMilliseconds(startDate);
        // Keep the original inclusive upper-bound semantics. A missing end
        // filter stays unbounded, just like the standard aggregations.
        const upperFilter = elasticQueryFilter.find(filter => filter.range && filter.range[dateField] && filter.range[dateField].lte !== undefined);
        const to = upperFilter ? toMilliseconds(upperFilter.range[dateField].lte) : null;
        let scanFilters = elasticQueryFilter.map(filter => filter);
        if (deltaOptions.contextStartDate !== undefined) {
            const context = toMilliseconds(deltaOptions.contextStartDate);
            if (context === null || from === null || context > from) throw new Error("Delta contextStartDate must be at or before startDate");
            scanFilters = scanFilters.map(filter => {
                if (!filter.range || !filter.range[dateField] || filter.range[dateField].gte === undefined) return filter;
                return { range: { [dateField]: { ...filter.range[dateField], gte: getElasticTimestamp(new Date(context), dateField) } } };
            });
        }
        const deltaAggregation = buildDeltaAggregation(deltaRequests, mapIntervalToES(interval), deltaOptions, from, to);
        elasticQuery.aggs[DELTA_AGGREGATION_NAME] = deltaOptions.contextStartDate === undefined ? deltaAggregation : {
            global: {},
            aggs: {
                scope: {
                    filter: { bool: { must: elasticQueryMust, filter: scanFilters } },
                    aggs: { totals: deltaAggregation }
                }
            }
        };
        // Do not retrieve raw hits or calculate an unused hit count/sort.
        // The caller must use applyDeltaAggregations() on the search response.
        return { ...elasticQuery, size: 0, track_total_hits: false };
    }

    let elasticQuerySort = {};
    elasticQuerySort["@timestamp"] = "asc";
    return { ...elasticQuery, sort: elasticQuerySort };
}

// export const buildTimeRangeQuery = (startDate, endDate, interval, site_id, columns, filters, children_site_ids, eventsOnly = false) => {
//     let elasticQuery = {};
//     let elasticQueryMust = buildQueryMust(site_id, children_site_ids, filters);
//
//     let elasticQueryFilter = [];
//     if (eventsOnly) {
//         let exists = {};
//         exists["field"] = "events";
//         elasticQueryFilter.push({
//             exists,
//         });
//     }
//
//     let dateField = "@timestamp";
//
//
//     if (startDate || endDate) {
//         //elasticQueryFilter['range'] = { "Datetime": {} }
//         if (startDate) {
//             let start = Object.assign(startDate);
//             // start.setHours(start.getHours() - 1);
//             let gte = getElasticTimestamp(start, dateField)
//             let range = {};
//             range[dateField] = { gte };
//             elasticQueryFilter.push({
//                 range,
//             });
//         }
//
//         if (endDate) {
//             let end = Object.assign(endDate);
//             // end.setHours(end.getHours() + 1);
//             let lte = getElasticTimestamp(end, dateField);
//             let range = {};
//             range[dateField] = { lte };
//             elasticQueryFilter.push({
//                 range,
//             });
//         } else endDate = new Date();
//     }
//
//     let elasticQueryAggs;
//     if (interval && interval != "data") {
//
//         let elasticInterval = interval;
//         if (elasticInterval === "hour") {
//             elasticInterval = "1h";
//         } else if (elasticInterval === "minute") {
//             elasticInterval = "1m";
//         }
//
//
//         let elasticDateFormat = dateFormatFrenchPretty;
//         if (interval === "hour") {
//             elasticDateFormat = dateFormatFrenchPrettyWithMinutes;
//         } else if (interval === "minute") {
//             elasticDateFormat = dateFormatFrenchPrettyWithMinutes;
//         }
//
//         elasticQueryAggs = {
//             time_buckets: {
//                 date_histogram: {
//                     field: dateField,
//                     calendar_interval: elasticInterval,
//                     format: elasticDateFormat,
//                 },
//                 aggs: {},
//             },
//         };
//         for (let k in columns) {
//             let displayedSensor = columns[k];
//             let aggregationsType = "avg";
//             if (displayedSensor.aggregationsType == 'Moyenne') aggregationsType = 'avg';
//             else if (displayedSensor.aggregationsType == 'Cumul') aggregationsType = 'sum';
//             else if (displayedSensor.aggregationsType == 'Maximum') aggregationsType = 'max';
//
//             elasticQueryAggs.time_buckets.aggs[displayedSensor.csv_column] = {
//             };
//             elasticQueryAggs.time_buckets.aggs[displayedSensor.csv_column][aggregationsType] = {
//                 field: displayedSensor.csv_column,
//             }
//         }
//     }
//
//     elasticQuery = {
//         ...elasticQuery,
//         query: { bool: { must: elasticQueryMust, filter: elasticQueryFilter } },
//     };
//     if (elasticQueryAggs) {
//         elasticQuery = { ...elasticQuery, aggs: elasticQueryAggs };
//     }
//     let elasticQuerySort = {};
//     elasticQuerySort['@timestamp'] = "asc";
//     return { ...elasticQuery, sort: elasticQuerySort };
// }



const getElasticTimestamp = (date, format) => {
    let d = new Date(date);
    //GMT+1...
    // d.setHours(d.getHours() + 1);
    let timestamp;
    if (format == "@timestamp") {
        timestamp = convertDateToElasticTimeStamp(d);
        // timestamp = d.toISOString().split(".")[0].split("T")[0] + "T00:00:00.000Z";

    } else {
        timestamp = d.getTime() / 1000;
    }
    return timestamp;
}

